/**
 * PRD.269 — pure, permission-aware Home dashboard configuration.
 *
 * Declares which permission gates each Home element (top action, KPI, widget,
 * Clinic-Hub shortcut) and the pure visibility helpers used to filter them.
 * Kept React-free and dependency-light so it can be unit-tested with node:test
 * (see homeSections.test.ts). All gating reuses the PRD.268 resolver via an
 * injected `can(req)` predicate — during permission loading the real predicate
 * returns false, so nothing renders.
 */
import type { PermissionRequirement } from '@helios/authz';
import { MODULES, req, isPathAccessible } from '../../auth/permissionMap';

export type Can = (req: PermissionRequirement) => boolean;

/** A Home element gated by an explicit requirement and/or its destination route. */
export interface HomeElement {
  id: string;
  /** Explicit permission requirement (wins over `path`). */
  requiredPermission?: PermissionRequirement;
  /** Route-derived gating (via accessForPath), when no explicit requirement. */
  path?: string;
}

/** Presentational fields for elements the component renders directly. */
export interface HomeLinkElement extends HomeElement {
  label: string;
  icon: string;
  path: string;
  /** Rendered as the primary/filled button. */
  primary?: boolean;
}

// ── Top header actions ──
export const TOP_ACTIONS: HomeLinkElement[] = [
  {
    id: 'create-patient',
    label: 'Create Patient',
    icon: 'user-plus',
    path: '/patient/new',
    requiredPermission: req(MODULES.PATIENT_CREATION, 'Create Patient', 'edit'),
  },
  { id: 'open-queue', label: 'Open Queue', icon: 'list-ordered', path: '/clinic/queue' },
  {
    id: 'book-patient',
    label: 'Book a Patient',
    icon: 'calendar',
    // Opens the shared Book a Patient drawer in place (handled in the click
    // handler); path is retained only as a permission/fallback reference.
    path: '/appointments/new',
    requiredPermission: req(MODULES.APPOINTMENTS, 'Book a Patient', 'edit'),
    primary: true,
  },
];

/** Money privacy toggle — only meaningful with billing visibility. */
export const MONEY_TOGGLE: HomeElement = {
  id: 'money-toggle',
  requiredPermission: req(MODULES.BILLING, 'Billing'),
};

// ── Top KPI row ──
export const KPIS: HomeElement[] = [
  { id: 'patients-served', requiredPermission: req(MODULES.DASHBOARD, 'Dashboard') },
  { id: 'in-queue', requiredPermission: req(MODULES.QUEUE, 'Queue') },
  { id: 'collections', requiredPermission: req(MODULES.BILLING, 'Billing') },
  { id: 'new-outstanding', requiredPermission: req(MODULES.BILLING, 'Billing') },
  { id: 'ready-to-dispense', requiredPermission: req(MODULES.PHARMACY, 'Pharmacy Module') },
];

/** Patient Alerts — surfaced in the right rail, not the top KPI row. */
export const ALERTS_KPI: HomeElement = {
  id: 'patient-alerts',
  requiredPermission: req(MODULES.PATIENT, 'Patient Workspace'),
};

// ── Main widgets ──
export const WIDGETS: HomeElement[] = [
  { id: 'queue-snapshot', requiredPermission: req(MODULES.QUEUE, 'Queue') },
  { id: 'billing-snapshot', requiredPermission: req(MODULES.BILLING, 'Billing') },
  { id: 'next-patient', requiredPermission: req(MODULES.PATIENT_CONSULTATION, 'Consultation') },
  { id: 'schedule', requiredPermission: req(MODULES.APPOINTMENTS, 'Day View') },
  { id: 'recent-patients', requiredPermission: req(MODULES.PATIENT, 'Patient Workspace') },
];

/** "Start Encounter" inside the Next Patient widget is an edit action. */
export const START_ENCOUNTER: HomeElement = {
  id: 'start-encounter',
  requiredPermission: req(MODULES.PATIENT_CONSULTATION, 'Consultation', 'edit'),
};

// ── Clinic Hub shortcuts (route-derived) ──
export const HUB_ITEMS: HomeLinkElement[] = [
  { id: 'hub-patients', label: 'View Patients', icon: 'users', path: '/patients' },
  { id: 'hub-queue', label: 'Queue', icon: 'list-ordered', path: '/clinic/queue' },
  { id: 'hub-appointments', label: 'Appointments', icon: 'calendar', path: '/appointments/month' },
  { id: 'hub-inventory', label: 'Inventory', icon: 'layout-grid', path: '/inventory/items' },
  { id: 'hub-pharmacy', label: 'Pharmacy', icon: 'heart', path: '/inventory/pharmacy' },
];

/**
 * Whether a single element is visible: explicit requirement wins, else the
 * destination route decides, else visible. Fails closed (predicate returns
 * false while loading / on denial).
 */
export function isElementVisible(el: HomeElement, can: Can): boolean {
  if (el.requiredPermission) return can(el.requiredPermission);
  if (el.path) return isPathAccessible(el.path, can);
  return true;
}

/** Filter a list of elements to the visible ones (hides inaccessible entries). */
export function visibleElements<T extends HomeElement>(els: readonly T[], can: Can): T[] {
  return els.filter((el) => isElementVisible(el, can));
}

/** Set of visible element ids — convenient for id-keyed display objects. */
export function visibleIds(els: readonly HomeElement[], can: Can): Set<string> {
  return new Set(visibleElements(els, can).map((el) => el.id));
}
